import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { SelectDropdown } from '@womcorp/wom-miwom-ui-kit/dist/components/Select';
import {
  MiniErrorInternal,
  MiniErrorService,
} from '@womcorp/wom-miwom-ui-kit/dist/@theme/assets/svg/icons/nav';
import Button from '@womcorp/wom-miwom-ui-kit/dist/components/Button';
import {
  ErrorPaymentsIcon as ErrorIcon,
  InternalServerErrorIcon,
} from '@womcorp/wom-miwom-ui-kit/dist/@theme/assets/svg/pictures';
import { navigate } from 'gatsby';
import { useMediaQuery } from 'react-responsive';
import StatusBox from './StatusBox';
import useUser from '../../hooks/useUser';
import MultiAccountContainer from '../Home/MultiAccount/MultiAccount';
import InvoiceStatus from './InvoiceStatus';
import {
  ContainerStyled,
  ErrorContainerStyled,
  ErrorInfoContainerStyled,
  ErrorMonoContainerStyled,
  IconAndInfoContainerStyled,
} from './styles/StatusBarStyled';
import StatusSkeleton from './StatusSkeleton/StatusSkeleton';
import useSegment from '../../hooks/useSegment';
import ErrorTemplate from '../Errors/ErrorTemplate';
import PageContext from '../../contextProvider/PageContext';
import { GoogleTagManager } from '../../helpers';

const Status = ({
  invoicesStatus,
  handleAccount,
  loadingAccounts,
  loadingInvoices,
  accountList,
  accountSelected,
  retryFunc,
  isError,
  retryCount,
}) => {
  const { pathname } = useContext(PageContext);

  const isMobileDevice = useMediaQuery({
    query: '(max-device-width: 768px)',
  });

  const { user, paymentUrl } = useUser();
  const { isPrepaid } = useSegment();

  const showStatus = !isPrepaid && (!user.isCorporate || user.isAdmin);
  const isMultiAccountRestriction = accountList.length > 1 && user.isAdmin;

  const formattedPath = pathname === '/home' ? 'Home' : 'Mis Boletas';
  const pagePath = pathname.replace('/', '');
  useEffect(() => {
    if (
      !loadingInvoices &&
      isError &&
      !isMultiAccountRestriction &&
      pathname === '/mis-boletas'
    ) {
      if (retryCount <= 3) {
        GoogleTagManager.pushToDataLayer({
          event: 'pagina-virtual',
          page: '/mis-boletas/error-boletas',
          pagename: 'Mis boletas - Error Boletas',
        });
      } else {
        GoogleTagManager.pushToDataLayer({
          event: 'pagina-virtual',
          page: '/mis-boletas/error-boletas/error interno',
          pagename: 'Mis boletas - Error Boletas - Error interno',
        });
      }
    }

    if (!loadingInvoices && isError && isMultiAccountRestriction) {
      if (retryCount <= 3) {
        GoogleTagManager.pushToDataLayer({
          event: 'pagina-virtual',
          page: `/status-bar/${pagePath}/error-statusbar-otrascuentas`,
          pagename: `Status bar - ${formattedPath} - Error Otras Cuentas`,
        });
      } else {
        GoogleTagManager.pushToDataLayer({
          event: 'pagina-virtual',
          page: `/status-bar/${pagePath}/error-statusbar-otrascuentas`,
          pagename: `Status bar - ${formattedPath} - Error Otras Cuentas - Error interno de servicio`,
        });
      }
    }
  }, [loadingInvoices, isError, isMultiAccountRestriction, retryCount]);

  const AccountSelector = () => {
    const selector = (
      <SelectDropdown
        value={accountSelected}
        hint="Seleccionar cuenta"
        loading={loadingAccounts}
        name="accountSelector"
        selectors={accountList || []}
        onChange={handleAccount}
        style={{ height: 44 }}
        disabed={loadingAccounts}
        spinnerSize={23}
        spinnerWidth={5}
      />
    );
    return (
      <MultiAccountContainer
        selector={selector}
        isMultiAccount={isMultiAccountRestriction}
        status={invoicesStatus?.debtStatus}
      />
    );
  };

  const StatusContent = () => {
    return (
      <ContainerStyled accountSelector={isMultiAccountRestriction}>
        {isMultiAccountRestriction && <AccountSelector />}
        {loadingInvoices && (
          <StatusSkeleton isMultiAccount={accountList.length > 1} />
        )}
        {!loadingInvoices && invoicesStatus && (
          <InvoiceStatus
            status={invoicesStatus?.debtStatus}
            invoicesStatus={invoicesStatus}
            last6Invoices={{
              lastInvoice: invoicesStatus?.lastInvoice,
              lastSemester: invoicesStatus?.otherInvoices,
            }}
            paymentUrl={paymentUrl}
            accountSelected={accountSelected}
            isMultiAccount={isMultiAccountRestriction}
            isError={isError}
            retryCount={retryCount}
          />
        )}
        {!loadingInvoices &&
          isError &&
          isMultiAccountRestriction &&
          (retryCount <= 3 ? (
            <ErrorContainerStyled>
              <IconAndInfoContainerStyled>
                <div>
                  <MiniErrorService />
                </div>

                <ErrorInfoContainerStyled>
                  <h1>Ha ocurrido un error</h1>
                  <span>
                    No se ha podido completar tu solicitud, vuelve a intentarlo
                  </span>
                </ErrorInfoContainerStyled>
              </IconAndInfoContainerStyled>
              <Button
                onClick={retryFunc}
                style={{
                  minWidth: '0',
                  width: isMobileDevice ? '109px' : '100px',
                }}
              >
                Reintentar
              </Button>
            </ErrorContainerStyled>
          ) : (
            <ErrorContainerStyled>
              <IconAndInfoContainerStyled>
                <div>
                  <MiniErrorInternal />
                </div>

                <ErrorInfoContainerStyled>
                  <h1>Error Interno del Servidor</h1>
                  <span>Nuestro sistema ha fallado, vuelve más tarde</span>
                </ErrorInfoContainerStyled>
              </IconAndInfoContainerStyled>
              {pathname === '/mis-boletas' && (
                <Button
                  onClick={() => {
                    navigate('/home');
                  }}
                  style={{
                    minWidth: '0',
                    width: isMobileDevice ? '104px' : '88px',
                  }}
                >
                  Ir al inicio
                </Button>
              )}
            </ErrorContainerStyled>
          ))}
      </ContainerStyled>
    );
  };

  if (!showStatus) return null;

  if (
    !loadingInvoices &&
    isError &&
    !isMultiAccountRestriction &&
    pathname === '/mis-boletas'
  ) {
    if (retryCount <= 3) {
      return (
        <ErrorMonoContainerStyled>
          <ErrorTemplate
            icon={<ErrorIcon />}
            title="Ha ocurrido un error"
            onClickButton={retryFunc}
            onBackLinkClick={() => {
              navigate('/home');
            }}
            backText="Volver"
          >
            <span>
              No se ha podido completar tu solicitud, vuelve a intentarlo
            </span>
          </ErrorTemplate>
        </ErrorMonoContainerStyled>
      );
    }

    return (
      <ErrorMonoContainerStyled>
        <ErrorTemplate
          title="Error Interno del Servidor"
          icon={<InternalServerErrorIcon />}
          onClickButton={() => navigate('/home')}
          buttonText="Ir al inicio"
        >
          Nuestro sistema ha fallado, vuelve más tarde
        </ErrorTemplate>
      </ErrorMonoContainerStyled>
    );
  }

  if (
    !isMultiAccountRestriction &&
    invoicesStatus &&
    invoicesStatus?.debtStatus === 'no_bills' &&
    pathname === '/mis-boletas'
  ) {
    return null;
  }

  return (
    <StatusBox>
      <StatusContent />
    </StatusBox>
  );
};

export default Status;

Status.propTypes = {
  invoicesStatus: PropTypes.shape({
    debtStatus: PropTypes.string,
    amountToPay: PropTypes.number,
    idInvoice: PropTypes.string,
    lastInvoice: PropTypes.shape({
      amount: PropTypes.number,
      amountToPay: PropTypes.number,
      dueDate: PropTypes.string,
      month: PropTypes.string,
      documentId: PropTypes.string,
      status: PropTypes.string,
      urlFactura: PropTypes.string,
    }),
    otherInvoices: PropTypes.arrayOf(
      PropTypes.shape({
        amount: PropTypes.number,
        amountToPay: PropTypes.number,
        dueDate: PropTypes.string,
        month: PropTypes.string,
        documentId: PropTypes.string,
        status: PropTypes.string,
        urlFactura: PropTypes.string,
      })
    ),
  }).isRequired,
  handleAccount: PropTypes.func.isRequired,
  loadingAccounts: PropTypes.bool.isRequired,
  loadingInvoices: PropTypes.bool.isRequired,
  accountList: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ).isRequired,
  accountSelected: PropTypes.string.isRequired,
  retryFunc: PropTypes.func.isRequired,
  isError: PropTypes.bool.isRequired,
  retryCount: PropTypes.number.isRequired,
};
